import { MULTI_MARK_ANSWER } from '@ztechapps/quickscore.contract'
import {
  EXTRACTION_ANSWER_FORMAT_INSTRUCTIONS,
  EXTRACTION_BEST_GUESS_INSTRUCTION,
  EXTRACTION_SINGLE_SHEET_CONFIDENCE_INSTRUCTIONS,
  EXTRACTION_STUDENT_NAME_INSTRUCTIONS,
} from './extractionSharedInstructions'

/**
 * System prompt sent with a single photographed bubble (OMR) sheet, routed to assessments of
 * type `Bubble`, extracting the student name and the mark-detected answers. Interpolated with
 * `templateReplace` (`{{maxQuestion}}` token) before the vision call.
 *
 * Carries mark-detection instructions ONLY — no handwritten/numeric answer
 * transcription guidance, since a `Bubble` assessment's answer key accepts
 * `Mcq` items only and every answer is read off a shaded circle, never
 * transcribed. Compare `HANDWRITTEN_SHEET_PROMPT_DEFAULT`, which carries the
 * inverse: transcription instructions only.
 *
 * `data.confidence` is REQUIRED-AND-NULLABLE on `EXTRACTED_SHEET_SCHEMA` (`extractedSheetSchema.ts`),
 * the schema this route and the batch route share — omitting it from the documented payload here
 * fails the schema-constrained AI call with "No object generated: response did not match schema."
 *
 * The mark-detection instruction interpolates `MULTI_MARK_ANSWER` (`@ztechapps/quickscore.contract`)
 * verbatim rather than a hardcoded copy of the string, so the prompt and `toExtractedSheet`'s own
 * marker normalisation can never drift apart.
 */
export const BUBBLE_SHEET_PROMPT_DEFAULT = `You are an answer sheet grading assistant. Extract the student name and all answers from this bubble (OMR) answer sheet image - a shaded-circle form where each numbered item is one row of option circles.

Return ONLY a raw JSON object with this exact shape (no markdown fences, no commentary):
{
  "data": {
    "studentName": <string or null>,
    "confidence": <confidence 0-1 for this sheet>,
    "answers": [
      [<questionNumber>, "<answer>", <confidence 0-1>]
    ]
  },
  "confidence": <overall confidence 0-1>
}

${EXTRACTION_ANSWER_FORMAT_INSTRUCTIONS}

${EXTRACTION_SINGLE_SHEET_CONFIDENCE_INSTRUCTIONS}

Questions to extract: 1 to {{maxQuestion}}

${EXTRACTION_STUDENT_NAME_INSTRUCTIONS}

BUBBLE (OMR) SHEETS: each numbered item is ONE ROW of option circles labeled A, B, C, D (sometimes E). Work strictly one row at a time, left to right, and tie each circle to the PRINTED letter directly on/beside it - never read a mark from the row above or below, and never shift the answer to a neighbouring column. A pre-printed circle outline or a faint stray mark is NOT a selection - a circle counts if it carries heavy dark coverage, however messy, off-centre, or spilling past its edge. Before picking an answer, check that row for ambiguity: if two or more circles carry heavy dark coverage, return the exact text ${MULTI_MARK_ANSWER} as the answer for that item. Only when exactly one circle carries heavy dark coverage, return its printed letter (e.g. "B"). Always return that letter as UPPERCASE. Before returning, VERIFY the letter you give is the one physically printed at the circle you judged most filled. If no circle in that row is clearly filled, return an empty string for that item. Return each question's answer as its own separate [questionNumber, answer, confidence] array - never combine multiple questions into one array.

${EXTRACTION_BEST_GUESS_INSTRUCTION}`
